import React, { useState, useEffect, useCallback, useRef } from 'react';
import Comments from '../components/Comments';
import '../css.css';
import { useToast } from '../components/Toast';
import useAdminVerification from '../hooks/useAdminVerification';
import AdminSettingsPanel from '../components/AdminSettingsPanel';

// 최적화된 이미지 컴포넌트
const ItemImage = ({ filename, alt, className }) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    if (filename) {
      setImageError(false);
      // filename에 이미 .webp 확장자가 포함되어 있는지 확인
      const imageUrl = filename.endsWith('.webp')
        ? `/data/img/preview/${filename}`
        : `/data/img/preview/${filename}.webp`;
      setImageSrc(imageUrl);
    }
  }, [filename]);

  if (imageError || !filename) {
    return <div className="no-image-placeholder">이미지 없음</div>;
  }

  return (
    <img 
      src={imageSrc}
      alt={alt}
      className={className}
      onError={() => setImageError(true)}
      loading="lazy"
    />
  );
};

// 검색 가능한 Select 컴포넌트
const SearchableSelect = ({ 
  label,
  data,
  selectedItem,
  searchTerm,
  isOpen,
  onSearchChange,
  onItemSelect,
  onClearSelection,
  onToggleDropdown,
  placeholder,
  getDisplayName,
  getImageFilename,
  getItemDetails // 새로운 prop: 아이템의 세부 정보를 반환하는 함수
}) => {
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  
  // 외부 클릭 감지
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target) && isOpen) {
        onToggleDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onToggleDropdown]);
  
  // 검색 필터링
  const filteredData = data.filter(item => {
    if (!searchTerm) return true;
    const displayName = getDisplayName(item).toLowerCase();
    return displayName.includes(searchTerm.toLowerCase());
  });

  // 검색어 변경 핸들러
  const handleSearchChange = useCallback((value) => {
    onSearchChange(value);
  }, [onSearchChange]);

  return (
    <div className="item-select-container" ref={containerRef}>
      {/* <label className="item-select-label">{label}</label> */}
      
      <div className="item-select-row">
        <div className="item-image-container">
          {selectedItem && (
            <ItemImage 
              filename={getImageFilename(selectedItem)}
              alt={getDisplayName(selectedItem)}
              className="item-preview-image"
            />
          )}
        </div>
        <div className="searchable-select">
          <div className="select-input-container">
            <input
              ref={inputRef}
              type="text"
              className="select-search-input"
              placeholder={selectedItem ? getDisplayName(selectedItem) : placeholder}
              value={searchTerm || ''}
              onChange={(e) => handleSearchChange(e.target.value)}
              onFocus={() => onToggleDropdown(true)}
              autoComplete="off"
              spellCheck="false"
            />
            {selectedItem && (
              <button 
                type="button"
                className="clear-selection-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  onClearSelection();
                }}
                title="선택 해제"
              >
                ✕
              </button>
            )}
            <button 
              type="button"
              className="select-dropdown-btn"
              onClick={() => onToggleDropdown(!isOpen)}
            >
              <span className={`dropdown-arrow ${isOpen ? 'open' : ''}`}>▼</span>
            </button>
          </div>
          
          {isOpen && (
            <div className="select-dropdown">
              {filteredData.length === 0 ? (
                <div className="select-option no-results">결과가 없습니다</div>
              ) : (
                filteredData.map(item => (
                  <div
                    key={item.id || item.name}
                    className="select-option"
                    onClick={() => onItemSelect(item)}
                  >
                    <div className="option-content">
                      <ItemImage 
                        filename={getImageFilename(item)}
                        alt={getDisplayName(item)}
                        className="option-image"
                      />
                      <div className="option-info">
                        <span className="option-text">{getDisplayName(item)}</span>
                        {getItemDetails && (
                          <span className="option-details">{getItemDetails(item)}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// 마찰 브레이크 원형 게이지 컴포넌트
const DragGauge = ({ maxStage, currentStage }) => {
  const angle = (currentStage * 360) / 30; // 30단계 기준
  const radius = 45;
  const strokeWidth = 8;
  const normalizedRadius = radius - strokeWidth * 0.5;
  const circumference = normalizedRadius * 2 * Math.PI;
  const strokeDasharray = `${angle / 360 * circumference} ${circumference}`;

  return (
    <div className="drag-gauge">
      <svg
        height={radius * 2}
        width={radius * 2}
        className="drag-gauge-svg"
      >
        {/* 배경 원 */}
        <circle
          stroke="#e2e8f0"
          fill="transparent"
          strokeWidth={strokeWidth}
          r={normalizedRadius}
          cx={radius}
          cy={radius}
        />
        {/* 진행률 원 */}
        <circle
          stroke={currentStage <= maxStage ? "#10b981" : "#ef4444"}
          fill="transparent"
          strokeWidth={strokeWidth}
          strokeDasharray={strokeDasharray}
          strokeDashoffset={0}
          strokeLinecap="round"
          r={normalizedRadius}
          cx={radius}
          cy={radius}
          transform={`rotate(-90 ${radius} ${radius})`}
        />
      </svg>
      <div className="drag-gauge-text">
        <div className="drag-current">{currentStage}</div>
      </div>
    </div>
  );
};

// 위험 배너 컴포넌트
const DangerBanner = ({ message }) => (
  <div className="danger-banner">
    <div className="danger-icon">⚠️</div>
    <div className="danger-message">{message}</div>
  </div>
);

// 경고 배너 컴포넌트
const WarningBanner = ({ message }) => (
  <div className="warning-banner">
    <div className="warning-icon">⚠️</div>
    <div className="warning-message">{message}</div>
  </div>
);

const DurationPage = () => {
  const toast = useToast();
  const { isAdminVerified } = useAdminVerification();
  
  // 데이터 상태
  const [rodData, setRodData] = useState([]);
  const [reelData, setReelData] = useState([]);
  const [lineData, setLineData] = useState([]);

  // 선택된 아이템 상태
  const [selectedRod, setSelectedRod] = useState(null);
  const [selectedReel, setSelectedReel] = useState(null);
  const [selectedLine, setSelectedLine] = useState(null);

  // 검색어 상태
  const [rodSearchTerm, setRodSearchTerm] = useState('');
  const [reelSearchTerm, setReelSearchTerm] = useState('');
  const [lineSearchTerm, setLineSearchTerm] = useState('');

  // 드롭다운 열림 상태
  const [rodDropdownOpen, setRodDropdownOpen] = useState(false);
  const [reelDropdownOpen, setReelDropdownOpen] = useState(false);
  const [lineDropdownOpen, setLineDropdownOpen] = useState(false);

  // 저장/불러오기 관련 상태
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [savedSettings, setSavedSettings] = useState([]); // 저장된 설정 목록
  const [selectedSettingId, setSelectedSettingId] = useState(''); // 선택된 설정 ID
  const [saveSettingName, setSaveSettingName] = useState(''); // 저장할 설정 이름
  const [showSaveInput, setShowSaveInput] = useState(false); // 저장 입력창 표시 여부
  const [showDeleteModal, setShowDeleteModal] = useState(false); // 삭제 확인 모달 표시 여부
  const [deleteTargetId, setDeleteTargetId] = useState(''); // 삭제할 설정 ID
  const [showDuplicateModal, setShowDuplicateModal] = useState(false); // 중복 이름 경고 모달 표시 여부
  const [duplicateSettingName, setDuplicateSettingName] = useState(''); // 중복된 설정 이름

  // 관리자 전용 상태
  const [isAdmin, setIsAdmin] = useState(false); // 관리자 권한
  const [allUsers, setAllUsers] = useState([]); // 모든 유저 목록
  const [selectedUserId, setSelectedUserId] = useState(''); // 선택된 유저 ID
  const [selectedUserSettings, setSelectedUserSettings] = useState([]); // 선택된 유저의 설정 목록
  const [selectedUserSettingId, setSelectedUserSettingId] = useState(''); // 선택된 유저의 설정 ID

  // 손상도 입력 상태
  const [rodDamage, setRodDamage] = useState('');
  const [reelMechanismDamage, setReelMechanismDamage] = useState('');
  const [reelFrictionDamage, setReelFrictionDamage] = useState('');
  const [lineDamage, setLineDamage] = useState('');
  
  // 목줄 개수 및 데이터 상태
  const [leaderCount, setLeaderCount] = useState(1);
  const [leaders, setLeaders] = useState([
    { weight: '', damage: '' },
    { weight: '', damage: '' },
    { weight: '', damage: '' },
    { weight: '', damage: '' }
  ]);

  // 계산 결과 상태
  const [rodResult, setRodResult] = useState(null);
  const [reelDragResult, setReelDragResult] = useState(null);
  const [reelCapacityResult, setReelCapacityResult] = useState(null);
  const [lineResult, setLineResult] = useState(null);
  const [leaderResults, setLeaderResults] = useState([null, null, null, null]);

  // API 데이터 가져오기
  useEffect(() => {
    const fetchData = async () => {
      try {
        // 낚싯대 데이터 가져오기
        const rodResponse = await fetch('/api/rod-sizes');
        if (rodResponse.ok) {
          const rodData = await rodResponse.json();
          setRodData(rodData);
        }

        // 릴 데이터 가져오기
        const reelResponse = await fetch('/api/reel-sizes');
        if (reelResponse.ok) {
          const reelData = await reelResponse.json();
          setReelData(reelData);
        }

        // 낚싯줄 데이터 가져오기
        const lineResponse = await fetch('/api/line');
        if (lineResponse.ok) {
          const lineData = await lineResponse.json();
          setLineData(lineData);
        }
      } catch (error) {
        console.error('데이터 가져오기 실패:', error);
      }
    };

    fetchData();
  }, []);

  // 저장된 설정 로드
  const loadSavedSettings = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setIsLoggedIn(false);
      return;
    }

    try {
      const response = await fetch('/api/tackle/saved-settings', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setSavedSettings(result.data);
        } else {
          console.error('저장된 설정 로드 실패:', result.error);
        }
      } else if (response.status === 401 || response.status === 403) {
        localStorage.removeItem('token');
        setIsLoggedIn(false);
        setSavedSettings([]);
      } else {
        console.error('저장된 설정 로드 실패:', response.status);
      }
    } catch (error) {
      console.error('저장된 설정 로드 실패:', error);
    }
  }, []); // useCallback 의존성 배열

  // 로그인 상태 확인
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      setIsLoggedIn(true);
      loadSavedSettings();
    } else {
      setIsLoggedIn(false);
      setSavedSettings([]);
    }
  }, [loadSavedSettings]);

  // 설정 저장
  const handleSaveSetting = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setIsLoggedIn(false);
      toast.error('로그인이 필요합니다.');
      return;
    }

    if (!saveSettingName.trim()) {
      toast.error('저장할 이름을 입력해주세요.');
      return;
    }

    if (!selectedRod && !selectedReel && !selectedLine) {
      toast.error('낚싯대, 릴, 낚싯줄 중 최소 하나는 선택해야 합니다.');
      return;
    }

    // 중복 이름 확인
    const trimmedName = saveSettingName.trim();
    const duplicateSetting = savedSettings.find(setting => 
      setting.name.toLowerCase() === trimmedName.toLowerCase()
    );

    if (duplicateSetting) {
      setDuplicateSettingName(trimmedName);
      setShowDuplicateModal(true);
      return;
    }

    await performSave();
  };

  // 관리자 권한 확인
  useEffect(() => {
    if (isLoggedIn) {
      setIsAdmin(isAdminVerified);
    } else {
      setIsAdmin(false);
    }
  }, [isLoggedIn, isAdminVerified]);

  // 관리자인 경우 유저 목록 로드
  useEffect(() => {
    if (isLoggedIn && isAdmin) {
      loadAllUsers();
    }
  }, [isLoggedIn, isAdmin]);

  // 관리자 전용: 모든 유저 목록 로드
  const loadAllUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch('/api/tackle/admin/users-with-settings', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setAllUsers(result.data);
        }
      }
    } catch (error) {
      console.error('유저 목록 로드 실패:', error);
    }
  };

  // 관리자 전용: 특정 유저의 설정 목록 로드
  const loadUserSettings = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`/api/tackle/admin/user-settings/${userId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setSelectedUserSettings(result.data);
        }
      }
    } catch (error) {
      console.error('유저 설정 로드 실패:', error);
    }
  };

  // 관리자 전용: 유저 선택 핸들러
  const handleUserSelect = (userId) => {
    setSelectedUserId(userId);
    setSelectedUserSettingId('');
    setSelectedUserSettings([]);
    if (userId) {
      loadUserSettings(userId);
    }
  };

  // 관리자 전용: 유저의 설정 불러오기
  const handleLoadUserSetting = async (settingId) => {
    const setting = selectedUserSettings.find(s => s.id === parseInt(settingId));
    if (!setting) return;

    // 낚싯대 복원 (id 우선, 없으면 rod_size 이름으로 매칭)
    if (setting.rod_id) {
      const rod = rodData.find(r => r.id === setting.rod_id);
      if (rod) setSelectedRod(rod);
      else setSelectedRod(null);
    } else if (setting.rod_size) {
      const rod = rodData.find(r => r.size_name === setting.rod_size);
      if (rod) setSelectedRod(rod);
      else setSelectedRod(null);
    } else {
      setSelectedRod(null);
    }

    // 릴 복원 (id 우선, 없으면 reel_size 이름으로 매칭)
    if (setting.reel_id) {
      const reel = reelData.find(r => r.id === setting.reel_id);
      if (reel) setSelectedReel(reel);
      else setSelectedReel(null);
    } else if (setting.reel_size) {
      const reel = reelData.find(r => r.size_name === setting.reel_size);
      if (reel) setSelectedReel(reel);
      else setSelectedReel(null);
    } else {
      setSelectedReel(null);
    }

    // 낚싯줄 복원
    if (setting.line_id) {
      const line = lineData.find(l => l.id === setting.line_id);
      if (line) setSelectedLine(line);
      else setSelectedLine(null);
    } else {
      setSelectedLine(null);
    }

    // 손상도 복원
    setRodDamage(setting.rod_damage || '');
    setReelMechanismDamage(setting.reel_mechanism_damage || '');
    setReelFrictionDamage(setting.reel_friction_damage || '');
    setLineDamage(setting.line_damage || '');

    // 목줄 정보 복원
    setLeaderCount(setting.leader_count || 1);
    const newLeaders = [
      { weight: setting.leader1_weight || '', damage: setting.leader1_damage || '' },
      { weight: setting.leader2_weight || '', damage: setting.leader2_damage || '' },
      { weight: setting.leader3_weight || '', damage: setting.leader3_damage || '' },
      { weight: setting.leader4_weight || '', damage: setting.leader4_damage || '' }
    ];
    setLeaders(newLeaders);

    // 결과 초기화
    setRodResult(null);
    setReelDragResult(null);
    setReelCapacityResult(null);
    setLineResult(null);
    setLeaderResults([null, null, null, null]);

    const user = allUsers.find(u => u.user_id === selectedUserId);
    const username = user ? user.username : '사용자';
    toast.success(`${username}의 "${setting.name}" 설정을 불러왔습니다.`);
  };

  // 실제 저장 수행 함수
  const performSave = async () => {
    const token = localStorage.getItem('token');

    try {
      const response = await fetch('/api/tackle/save-setting', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: saveSettingName.trim(),
          rodId: selectedRod?.id || null,
          reelId: selectedReel?.id || null,
          lineId: selectedLine?.id || null,
          rodSize: selectedRod?.size_name || null,
          reelSize: selectedReel?.size_name || null,
          rodDamage: rodDamage || null,
          reelMechanismDamage: reelMechanismDamage || null,
          reelFrictionDamage: reelFrictionDamage || null,
          lineDamage: lineDamage || null,
          leaderCount: leaderCount || 1,
          leaders: leaders,
          notes: ''
        })
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          toast.success(result.message);
          setSaveSettingName('');
          setShowSaveInput(false);
          loadSavedSettings();
        } else {
          toast.error(result.error);
        }
      } else if (response.status === 401 || response.status === 403) {
        localStorage.removeItem('token');
        setIsLoggedIn(false);
        toast.error('로그인이 만료되었습니다. 다시 로그인해주세요.');
      } else {
        toast.error('설정 저장에 실패했습니다.');
      }
    } catch (error) {
      console.error('설정 저장 실패:', error);
      toast.error('설정 저장에 실패했습니다.');
    }
  };

  // 중복 확인 모달에서 덮어쓰기 확정
  const confirmOverwrite = async () => {
    setShowDuplicateModal(false);
    await performSave();
  };

  // 중복 확인 모달에서 취소
  const cancelOverwrite = () => {
    setShowDuplicateModal(false);
    setDuplicateSettingName('');
  };

  // 설정 불러오기
  const handleLoadSetting = async (settingId) => {
    const setting = savedSettings.find(s => s.id === parseInt(settingId));
    if (!setting) return;

    // 낚싯대 복원 (id 우선, 없으면 rod_size 이름으로 매칭)
    if (setting.rod_id) {
      const rod = rodData.find(r => r.id === setting.rod_id);
      if (rod) setSelectedRod(rod);
      else setSelectedRod(null);
    } else if (setting.rod_size) {
      const rod = rodData.find(r => r.size_name === setting.rod_size);
      if (rod) setSelectedRod(rod);
      else setSelectedRod(null);
    } else {
      setSelectedRod(null);
    }

    // 릴 복원 (id 우선, 없으면 reel_size 이름으로 매칭)
    if (setting.reel_id) {
      const reel = reelData.find(r => r.id === setting.reel_id);
      if (reel) setSelectedReel(reel);
      else setSelectedReel(null);
    } else if (setting.reel_size) {
      const reel = reelData.find(r => r.size_name === setting.reel_size);
      if (reel) setSelectedReel(reel);
      else setSelectedReel(null);
    } else {
      setSelectedReel(null);
    }

    // 낚싯줄 복원
    if (setting.line_id) {
      const line = lineData.find(l => l.id === setting.line_id);
      if (line) setSelectedLine(line);
      else setSelectedLine(null);
    } else {
      setSelectedLine(null);
    }

    // 손상도 복원
    setRodDamage(setting.rod_damage || '');
    setReelMechanismDamage(setting.reel_mechanism_damage || '');
    setReelFrictionDamage(setting.reel_friction_damage || '');
    setLineDamage(setting.line_damage || '');

    // 목줄 정보 복원
    setLeaderCount(setting.leader_count || 1);
    const newLeaders = [
      { weight: setting.leader1_weight || '', damage: setting.leader1_damage || '' },
      { weight: setting.leader2_weight || '', damage: setting.leader2_damage || '' },
      { weight: setting.leader3_weight || '', damage: setting.leader3_damage || '' },
      { weight: setting.leader4_weight || '', damage: setting.leader4_damage || '' }
    ];
    setLeaders(newLeaders);

    // 결과 초기화
    setRodResult(null);
    setReelDragResult(null);
    setReelCapacityResult(null);
    setLineResult(null);
    setLeaderResults([null, null, null, null]);

    toast.success(`"${setting.name}" 설정을 불러왔습니다.`);
  };

  // 설정 삭제 확인 모달 열기
  const openDeleteModal = (settingId) => {
    setDeleteTargetId(settingId);
    setShowDeleteModal(true);
  };

  // 설정 삭제 확인
  const confirmDeleteSetting = async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setIsLoggedIn(false);
        setShowDeleteModal(false);
        setDeleteTargetId('');
        toast.error('로그인이 필요합니다.');
        return;
      }

      const response = await fetch(`/api/tackle/saved-settings/${deleteTargetId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          toast.success(result.message);
          loadSavedSettings();
          if (selectedSettingId === deleteTargetId) {
            setSelectedSettingId('');
          }
        } else {
          toast.error(result.error);
        }
      } else if (response.status === 401 || response.status === 403) {
        localStorage.removeItem('token');
        setIsLoggedIn(false);
        toast.error('로그인이 만료되었습니다.');
      } else {
        toast.error('설정 삭제에 실패했습니다.');
      }
    } catch (error) {
      console.error('설정 삭제 실패:', error);
      toast.error('설정 삭제에 실패했습니다.');
    } finally {
      setShowDeleteModal(false);
      setDeleteTargetId('');
    }
  };

  // 설정 삭제 취소
  const cancelDeleteSetting = () => {
    setShowDeleteModal(false);
    setDeleteTargetId('');
  };

  // URL 파라미터에서 설정 복원 (최초 1회만)
  const isInitialMount = useRef(true);
  const hasRestoredFromUrl = useRef(false);

  useEffect(() => {
    if (rodData.length === 0 || reelData.length === 0 || lineData.length === 0) return;
    if (hasRestoredFromUrl.current) return;

    const params = new URLSearchParams(window.location.search);
    const rodId = params.get('r');
    const reelId = params.get('e');
    const lineId = params.get('l');
    const rodDmg = params.get('rd');
    const reelMechDmg = params.get('rm');
    const reelFricDmg = params.get('rf');
    const lineDmg = params.get('ld');
    const leaderCnt = params.get('lc');
    const leader1Wt = params.get('l1w');
    const leader1Dmg = params.get('l1d');
    const leader2Wt = params.get('l2w');
    const leader2Dmg = params.get('l2d');
    const leader3Wt = params.get('l3w');
    const leader3Dmg = params.get('l3d');
    const leader4Wt = params.get('l4w');
    const leader4Dmg = params.get('l4d');

    let restoredCount = 0;

    if (rodId) {
      // ID로 먼저 찾고, 없으면 name으로 찾기
      const rod = rodData.find(r => 
        (r.id && r.id.toString() === rodId) || 
        (r.size_name === rodId)
      );
      if (rod) {
        setSelectedRod(rod);
        restoredCount++;
      }
    }

    if (reelId) {
      // ID로 먼저 찾고, 없으면 name으로 찾기
      const reel = reelData.find(r => 
        (r.id && r.id.toString() === reelId) || 
        (r.size_name === reelId)
      );
      if (reel) {
        setSelectedReel(reel);
        restoredCount++;
      }
    }

    if (lineId) {
      // ID로 먼저 찾고, 없으면 name으로 찾기
      const line = lineData.find(l => 
        (l.id && l.id.toString() === lineId) || 
        (l.name === lineId)
      );
      if (line) {
        setSelectedLine(line);
        restoredCount++;
      }
    }

    if (rodDmg) {
      setRodDamage(rodDmg);
      restoredCount++;
    }

    if (reelMechDmg) {
      setReelMechanismDamage(reelMechDmg);
      restoredCount++;
    }

    if (reelFricDmg) {
      setReelFrictionDamage(reelFricDmg);
      restoredCount++;
    }

    if (lineDmg) {
      setLineDamage(lineDmg);
      restoredCount++;
    }

    // 목줄 개수 복원
    if (leaderCnt) {
      const count = parseInt(leaderCnt);
      if (count >= 1 && count <= 4) {
        setLeaderCount(count);
        restoredCount++;
      }
    }

    // 목줄 데이터 복원
    const newLeaders = [...leaders];
    if (leader1Wt || leader1Dmg) {
      newLeaders[0] = { weight: leader1Wt || '', damage: leader1Dmg || '' };
      restoredCount++;
    }
    if (leader2Wt || leader2Dmg) {
      newLeaders[1] = { weight: leader2Wt || '', damage: leader2Dmg || '' };
      restoredCount++;
    }
    if (leader3Wt || leader3Dmg) {
      newLeaders[2] = { weight: leader3Wt || '', damage: leader3Dmg || '' };
      restoredCount++;
    }
    if (leader4Wt || leader4Dmg) {
      newLeaders[3] = { weight: leader4Wt || '', damage: leader4Dmg || '' };
      restoredCount++;
    }
    setLeaders(newLeaders);

    if (restoredCount > 0) {
      hasRestoredFromUrl.current = true;
      toast.success(`공유된 설정을 불러왔습니다! (${restoredCount}개 항목)`, {
        duration: 3000
      });
    } else {
      // URL 파라미터가 없으면 복원 완료로 표시
      hasRestoredFromUrl.current = true;
    }
  }, [rodData, reelData, lineData, leaders, toast]);

  // 선택된 아이템이나 손상도가 변경될 때 URL 업데이트
  useEffect(() => {
    // 데이터가 로드되지 않았으면 업데이트하지 않음
    if (rodData.length === 0 || reelData.length === 0 || lineData.length === 0) {
      return;
    }

    // URL 복원이 완료될 때까지 대기
    if (!hasRestoredFromUrl.current) {
      return;
    }

    // 복원 완료 후 첫 렌더링은 스킵
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const params = new URLSearchParams();

    // 각 항목의 고유 식별자 사용 (name을 fallback으로)
    if (selectedRod) {
      const rodId = selectedRod.id || selectedRod.size_name;
      if (rodId) params.set('r', rodId);
    }
    if (selectedReel) {
      const reelId = selectedReel.id || selectedReel.size_name;
      if (reelId) params.set('e', reelId);
    }
    if (selectedLine) {
      const lineId = selectedLine.id || selectedLine.name;
      if (lineId) params.set('l', lineId);
    }
    if (rodDamage && rodDamage !== '') params.set('rd', rodDamage);
    if (reelMechanismDamage && reelMechanismDamage !== '') params.set('rm', reelMechanismDamage);
    if (reelFrictionDamage && reelFrictionDamage !== '') params.set('rf', reelFrictionDamage);
    if (lineDamage && lineDamage !== '') params.set('ld', lineDamage);
    
    // 목줄 개수 저장
    params.set('lc', leaderCount.toString());
    
    // 목줄 데이터 저장
    leaders.forEach((leader, index) => {
      const num = index + 1;
      if (leader.weight && leader.weight !== '') params.set(`l${num}w`, leader.weight);
      if (leader.damage && leader.damage !== '') params.set(`l${num}d`, leader.damage);
    });

    const newUrl = params.toString()
      ? `${window.location.pathname}?${params.toString()}`
      : window.location.pathname;

    window.history.replaceState({}, '', newUrl);
  }, [selectedRod, selectedReel, selectedLine, rodDamage, reelMechanismDamage, reelFrictionDamage, lineDamage, leaderCount, leaders, rodData, reelData, lineData]);

  // 계산 함수들
  const calculateRodDurability = () => {
    if (!selectedRod) {
      setRodResult(null);
      return;
    }

    const maxLoad = parseFloat(selectedRod.max_load);
    const damagePercent = parseFloat(rodDamage) || 0; // 기본값 0
    //const remainingLoad = maxLoad * ((100 - damagePercent) / 100);
    // 릴 하중 계산식 사용
    const remainingLoad = maxLoad * 0.3 + ((100 - damagePercent) / 100) * maxLoad * 0.7;

    setRodResult({
      original: maxLoad,
      remaining: remainingLoad,
      percentage: (100 - damagePercent)
    });
  };

  const calculateReelDurability = () => {
    if (!selectedReel) {
      setReelDragResult(null);
      setReelCapacityResult(null);
      return;
    }

    // 최대저항 계산
    const maxDrag = parseFloat(selectedReel.drag_max_value);
    const frictionDamagePercent = parseFloat(reelFrictionDamage) || 0; // 기본값 0
    const remainingDrag = maxDrag * ((100 - frictionDamagePercent) / 100);

    setReelDragResult({
      original: maxDrag,
      remaining: remainingDrag,
      percentage: (100 - frictionDamagePercent),
      perUnit: remainingDrag / 29 // 마찰 브레이크 1당 하중
    });

    // 내부하중 계산
    const internalLoad = parseFloat(selectedReel.internal_load_value);
    const mechanismDamagePercent = parseFloat(reelMechanismDamage) || 0; // 기본값 0
    const remainingCapacity = internalLoad * 0.3 + ((100 - mechanismDamagePercent) / 100) * internalLoad * 0.7;

    setReelCapacityResult({
      original: internalLoad,
      remaining: remainingCapacity,
      percentage: (remainingCapacity / internalLoad) * 100
    });
  };

  const calculateLineDurability = () => {
    if (!selectedLine) {
      setLineResult(null);
      return;
    }

    const maxWeight = parseFloat(selectedLine.weight_max);
    const damagePercent = parseFloat(lineDamage) || 0; // 기본값 0
    const remainingWeight = maxWeight * ((100 - damagePercent) / 100);

    setLineResult({
      original: maxWeight,
      remaining: remainingWeight,
      percentage: (100 - damagePercent)
    });
  };

  const calculateLeaderDurability = () => {
    const newResults = leaders.map((leader, index) => {
      if (index >= leaderCount || !leader.weight) {
        return null;
      }

      const maxWeight = parseFloat(leader.weight);
      const damagePercent = parseFloat(leader.damage) || 0; // 기본값 0
      const remainingWeight = maxWeight * ((100 - damagePercent) / 100);

      return {
        original: maxWeight,
        remaining: remainingWeight,
        percentage: (100 - damagePercent)
      };
    });

    setLeaderResults(newResults);
  };

  // 전체 계산 실행
  const calculateAll = () => {
    calculateRodDurability();
    calculateReelDurability();
    calculateLineDurability();
    calculateLeaderDurability();
  };

  // 입력값이 변경될 때마다 자동 계산
  useEffect(() => {
    calculateAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRod, rodDamage, selectedReel, reelMechanismDamage, reelFrictionDamage, selectedLine, lineDamage, leaderCount, leaders]);

  // 위험 상황 체크
  const checkDangerSituation = () => {
    if (!reelDragResult) return null;
    
    const reelDrag = reelDragResult.remaining;
    const dangers = [];
    const warnings = [];

    // 릴 최대저항과 낚싯대 하중 비교
    if (rodResult) {
      const rodRemaining = rodResult.remaining;
      const difference = Math.abs(reelDrag - rodRemaining);
      const percentage = (difference / Math.max(reelDrag, rodRemaining)) * 100;
      
      if (reelDrag > rodRemaining) {
        dangers.push(`릴 최대 저항(${reelDrag.toFixed(2)}kg)이 낚싯대 하중(${rodRemaining.toFixed(2)}kg)을 초과합니다!`);
      } else if (percentage <= 5) {
        warnings.push(`릴 최대 저항(${reelDrag.toFixed(2)}kg)과 낚싯대 하중(${rodRemaining.toFixed(2)}kg)의 차이가 5% 이내입니다.`);
      }
    }

    // 릴 최대저항과 낚싯줄 하중 비교
    if (lineResult) {
      const lineRemaining = lineResult.remaining;
      const difference = Math.abs(reelDrag - lineRemaining);
      const percentage = (difference / Math.max(reelDrag, lineRemaining)) * 100;
      
      if (reelDrag > lineRemaining) {
        dangers.push(`릴 최대 저항(${reelDrag.toFixed(2)}kg)이 낚싯줄 하중(${lineRemaining.toFixed(2)}kg)을 초과합니다!`);
      } else if (percentage <= 5) {
        warnings.push(`릴 최대 저항(${reelDrag.toFixed(2)}kg)과 낚싯줄 하중(${lineRemaining.toFixed(2)}kg)의 차이가 5% 이내입니다.`);
      }
    }

    // 릴 최대저항과 목줄 하중 비교 (모든 목줄)
    leaderResults.forEach((result, index) => {
      if (result && index < leaderCount) {
        const leaderRemaining = result.remaining;
        const difference = Math.abs(reelDrag - leaderRemaining);
        const percentage = (difference / Math.max(reelDrag, leaderRemaining)) * 100;
        
        if (reelDrag > leaderRemaining) {
          dangers.push(`릴 최대 저항(${reelDrag.toFixed(2)}kg)이 목줄${index + 1} 하중(${leaderRemaining.toFixed(2)}kg)을 초과합니다!`);
        } else if (percentage <= 5) {
          warnings.push(`릴 최대 저항(${reelDrag.toFixed(2)}kg)과 목줄${index + 1} 하중(${leaderRemaining.toFixed(2)}kg)의 차이가 5% 이내입니다.`);
        }
      }
    });

    return { dangers: dangers.length > 0 ? dangers : null, warnings: warnings.length > 0 ? warnings : null };
  };

  // 사용 가능한 마찰 브레이크 단계 계산
  const getUsableDragStage = () => {
    if (!reelDragResult) return null;

    // 모든 목줄의 최소값 구하기
    const leaderMinWeight = leaderResults
      .slice(0, leaderCount)
      .filter(result => result !== null)
      .reduce((min, result) => Math.min(min, result.remaining), Infinity);

    const minWeight = Math.min(
      rodResult?.remaining || Infinity,
      lineResult?.remaining || Infinity,
      leaderMinWeight
    );

    if (minWeight === Infinity) return null;

    const usableStage = Math.floor(minWeight / reelDragResult.perUnit);
    return Math.min(29, Math.max(1, usableStage)); // 1-29 범위로 제한
  };

  // 진행률 바 색상 결정
  const getBarColor = (percentage) => {
    if (percentage > 80) return '#1E90FF'; // 파랑
    if (percentage > 40) return '#FFA500'; // 주황
    return '#FF4500'; // 빨강
  };

  // 텐션 게이지 색상 계산 (백분율 기준)
  const getTensionGaugeColor = (percentage) => {
    if (percentage <= 48) {
      return '#10b981'; // 녹색 (안전) - 0~48%
    } else if (percentage <= 75) {
      return '#f59e0b'; // 노란색 (주의) - 48~75%
    } else {
      return '#ef4444'; // 빨간색 (위험) - 75~100%
    }
  };

  // 디스플레이 이름 가져오기 함수들
  const getRodDisplayName = (rod) => rod ? rod.size_name : '';
  const getReelDisplayName = (reel) => reel ? reel.size_name : '';
  const getLineDisplayName = (line) => line ? `${line.name} (${line.diameter}mm)` : '';

  // 이미지 파일명 가져오기 함수들
  const getRodImageFilename = (rod) => rod ? rod.filename : '';
  const getReelImageFilename = (reel) => reel ? reel.filename : '';
  const getLineImageFilename = (line) => line ? line.filename : '';

  // 세부 정보 가져오기 함수들
  const getRodDetails = (rod) => {
    if (!rod) return '';
    return `하중: ${rod.max_load}kg`;
  };

  const getReelDetails = (reel) => {
    if (!reel) return '';
    return `최대 저항: ${reel.drag_max_value} · 감는 속도: ${reel.ratio_value}`;
  };

  const getLineDetails = (line) => {
    if (!line) return '';
    return `종류: ${line.type} · 하중: ${line.weight_max}kg · 길이: ${line.length}m`;
  };

  // 모든 damage-input number input에 native wheel 이벤트로 스크롤 방지 및 값 변경
  useEffect(() => {
    const handleWheelOnNumberInputs = (e) => {
      // number input이고 damage-input 내부에 있는 경우
      if (e.target.type === 'number' && e.target.closest('.damage-input')) {
        e.stopPropagation(); // 상위 요소로의 전파 차단
        
        // 포커스되지 않은 경우 포커스 설정 후 값 변경
        if (document.activeElement !== e.target) {
          e.target.focus();
          
          // 휠 방향에 따라 값 변경
          const currentValue = parseFloat(e.target.value) || 0;
          const step = parseFloat(e.target.step) || 1;
          const min = parseFloat(e.target.min) || 0;
          const max = parseFloat(e.target.max) || 100;
          
          let newValue;
          if (e.deltaY < 0) {
            // 휠을 위로 돌리면 값 증가
            newValue = Math.min(currentValue + step, max);
          } else {
            // 휠을 아래로 돌리면 값 감소  
            newValue = Math.max(currentValue - step, min);
          }
          
          // React의 onChange 이벤트 시뮬레이션
          const event = new Event('input', { bubbles: true });
          e.target.value = newValue.toString();
          e.target.dispatchEvent(event);
        }
      }
    };

    // document에서 캡처 단계에서 이벤트 처리
    document.addEventListener('wheel', handleWheelOnNumberInputs, { 
      passive: false, 
      capture: true 
    });

    return () => {
      document.removeEventListener('wheel', handleWheelOnNumberInputs, { capture: true });
    };
  }, []);

  return (
    <>
      <h1 className="page-title">채비 계산기</h1>
      <div className="page-content">
        <div className='page-info'>
          <p>계산된 수치는 패치등으로 인해 인게임과 다를 수 있습니다.</p>
          <p>참고용으로만 사용 부탁드립니다.</p>
          <p>각 장비를 선택하고 손상도를 입력하면 실제 내구도가 계산됩니다.</p>
        </div>

        {/* 저장/불러오기 섹션 */}
        {isLoggedIn && (
          <div className="saved-settings-panel">
            <div className="settings-controls">
              <label>저장/불러오기</label>
              <div className="settings-row">
                {/* 불러오기 선택 */}
                <div className="load-controls">
                  <div className="select-with-delete">
                    <select
                      value={selectedSettingId}
                      onChange={(e) => {
                        const settingId = e.target.value;
                        setSelectedSettingId(settingId);
                        if (settingId) {
                          handleLoadSetting(settingId);
                        }
                      }}
                      className="settings-select"
                      disabled={savedSettings.length === 0 || selectedUserId !== ''}
                    >
                      <option value="">
                        {savedSettings.length === 0 ? '-- 저장된 채비가 없습니다 --' : '-- 저장된 채비 불러오기 --'}
                      </option>
                      {savedSettings.map(setting => (
                        <option key={setting.id} value={setting.id}>
                          {setting.name} ({new Date(setting.updated_at).toLocaleDateString()})
                        </option>
                      ))}
                    </select>
                    {selectedSettingId && selectedUserId === '' && (
                      <button 
                        className="delete-setting-btn"
                        onClick={() => openDeleteModal(selectedSettingId)}
                        title="선택한 설정 삭제"
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                </div>

                {/* 저장 버튼과 입력창 */}
                <div className="save-controls">
                  {!showSaveInput ? (
                    <button 
                      className="save-setting-btn"
                      onClick={() => setShowSaveInput(true)}
                      disabled={(!selectedRod && !selectedReel && !selectedLine) || selectedUserId !== ''}
                      title={selectedUserId !== '' ? '다른 유저의 채비를 보는 중에는 저장할 수 없습니다' : (!selectedRod && !selectedReel && !selectedLine ? '낚싯대, 릴, 낚싯줄 중 최소 하나는 선택해야 합니다' : '')}
                    >
                      💾 새 설정 저장
                    </button>
                  ) : (
                    <div className="save-input-container">
                      <input
                        type="text"
                        value={saveSettingName}
                        onChange={(e) => setSaveSettingName(e.target.value)}
                        placeholder="저장할 이름 입력..."
                        className="save-name-input"
                        maxLength={50}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleSaveSetting();
                          }
                        }}
                      />
                      <button 
                        className="save-confirm-btn"
                        onClick={handleSaveSetting}
                      >
                        저장
                      </button>
                      <button 
                        className="save-cancel-btn"
                        onClick={() => {
                          setShowSaveInput(false);
                          setSaveSettingName('');
                        }}
                      >
                        취소
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {selectedUserId !== '' && (
              <div className="admin-viewing-notice">
                ⚠️ 다른 유저의 채비를 보는 중입니다. 내 설정으로 돌아가려면 유저 선택을 해제하세요.
              </div>
            )}
          </div>
        )}

        {/* 관리자 전용: 다른 유저 채비 조회 패널 */}
        {isLoggedIn && isAdmin && (
          <AdminSettingsPanel
            allUsers={allUsers}
            selectedUserId={selectedUserId}
            onUserSelect={handleUserSelect}
            selectedUserSettings={selectedUserSettings}
            selectedUserSettingId={selectedUserSettingId}
            onSettingSelect={(settingId) => {
              setSelectedUserSettingId(settingId);
              if (settingId) {
                handleLoadUserSetting(settingId);
              }
            }}
            settingType="채비"
          />
        )}

        {/* 메인 레이아웃: 좌측 장비 설정, 우측 채비 정보 */}
        <div className="duration-main-layout">
          {/* 좌측: 장비 설정 영역 */}
          <div className="equipment-setup-area">
            {/* 낚싯대 섹션 */}
            <div className="equipment-section">
          <h2>낚싯대 내구도 계산</h2>
          
          <SearchableSelect
            label="낚싯대 선택"
            data={rodData}
            selectedItem={selectedRod}
            searchTerm={rodSearchTerm}
            isOpen={rodDropdownOpen}
            onSearchChange={setRodSearchTerm}
            onItemSelect={(rod) => {
              setSelectedRod(rod);
              setRodSearchTerm('');
              setRodDropdownOpen(false);
              setRodResult(null);
            }}
            onClearSelection={() => {
              setSelectedRod(null);
              setRodSearchTerm('');
              setRodResult(null);
            }}
            onToggleDropdown={setRodDropdownOpen}
            placeholder="낚싯대를 검색하여 선택하세요..."
            getDisplayName={getRodDisplayName}
            getImageFilename={getRodImageFilename}
            getItemDetails={getRodDetails}
          />

          {selectedRod && (
            <div className="damage-input-section">
              <div className="equipment-info">
                <p>최대 하중: {selectedRod.max_load} kg</p>
              </div>
              
              <div className="damage-input">
                <label>
                  손상도 (%):
                  <input 
                    type="number" 
                    value={rodDamage} 
                    onChange={(e) => {
                      let value = e.target.value;
                      if (value === '') {
                        setRodDamage('');
                        return;
                      }
                      const numValue = parseFloat(value);
                      if (!isNaN(numValue)) {
                        setRodDamage(Math.max(0, Math.min(100, numValue)).toString());
                      }
                    }}
                    min="0" 
                    max="100" 
                    placeholder="0-100"
                  />
                </label>
              </div>
            </div>
          )}
        </div>

        {/* 릴 섹션 */}
        <div className="equipment-section">
          <h2>릴 내구도 계산</h2>
          
          <SearchableSelect
            label="릴 선택"
            data={reelData}
            selectedItem={selectedReel}
            searchTerm={reelSearchTerm}
            isOpen={reelDropdownOpen}
            onSearchChange={setReelSearchTerm}
            onItemSelect={(reel) => {
              setSelectedReel(reel);
              setReelSearchTerm('');
              setReelDropdownOpen(false);
              setReelDragResult(null);
              setReelCapacityResult(null);
            }}
            onClearSelection={() => {
              setSelectedReel(null);
              setReelSearchTerm('');
              setReelDragResult(null);
              setReelCapacityResult(null);
            }}
            onToggleDropdown={setReelDropdownOpen}
            placeholder="릴을 검색하여 선택하세요..."
            getDisplayName={getReelDisplayName}
            getImageFilename={getReelImageFilename}
            getItemDetails={getReelDetails}
          />

          {selectedReel && (
            <div className="damage-input-section">
              <div className="equipment-info">
                <p>최대 저항: {selectedReel.drag_max_value}</p>
                <p>내부 하중: {selectedReel.internal_load_value}</p>
              </div>
              
              <div className="reel-damage-inputs">
                <div className="damage-input">
                  <label>
                    매커니즘 손상도 (%):
                    <input 
                      type="number" 
                      value={reelMechanismDamage} 
                      onChange={(e) => {
                        let value = e.target.value;
                        if (value === '') {
                          setReelMechanismDamage('');
                          return;
                        }
                        const numValue = parseFloat(value);
                        if (!isNaN(numValue)) {
                          setReelMechanismDamage(Math.max(0, Math.min(100, numValue)).toString());
                        }
                      }}
                      min="0" 
                      max="100" 
                      placeholder="0-100"
                    />
                  </label>
                </div>

                <div className="damage-input">
                  <label>
                    마찰 브레이크 손상도 (%):
                    <input 
                      type="number" 
                      value={reelFrictionDamage} 
                      onChange={(e) => {
                        let value = e.target.value;
                        if (value === '') {
                          setReelFrictionDamage('');
                          return;
                        }
                        const numValue = parseFloat(value);
                        if (!isNaN(numValue)) {
                          setReelFrictionDamage(Math.max(0, Math.min(100, numValue)).toString());
                        }
                      }}
                      min="0" 
                      max="100" 
                      placeholder="0-100"
                    />
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 낚싯줄 섹션 */}
        <div className="equipment-section">
          <h2>낚싯줄 내구도 계산</h2>
          
          <SearchableSelect
            label="낚싯줄 선택"
            data={lineData}
            selectedItem={selectedLine}
            searchTerm={lineSearchTerm}
            isOpen={lineDropdownOpen}
            onSearchChange={setLineSearchTerm}
            onItemSelect={(line) => {
              setSelectedLine(line);
              setLineSearchTerm('');
              setLineDropdownOpen(false);
              setLineResult(null);
            }}
            onClearSelection={() => {
              setSelectedLine(null);
              setLineSearchTerm('');
              setLineResult(null);
            }}
            onToggleDropdown={setLineDropdownOpen}
            placeholder="낚싯줄을 검색하여 선택하세요..."
            getDisplayName={getLineDisplayName}
            getImageFilename={getLineImageFilename}
            getItemDetails={getLineDetails}
          />

          {selectedLine && (
            <div className="damage-input-section">
              <div className="equipment-info">
                <p>최대 하중: {selectedLine.weight_max} kg</p>
                <p>굵기: {selectedLine.diameter} mm</p>
              </div>
              
              <div className="damage-input">
                <label>
                  손상도 (%):
                  <input 
                    type="number" 
                    value={lineDamage} 
                    onChange={(e) => {
                      let value = e.target.value;
                      if (value === '') {
                        setLineDamage('');
                        return;
                      }
                      const numValue = parseFloat(value);
                      if (!isNaN(numValue)) {
                        setLineDamage(Math.max(0, Math.min(100, numValue)).toString());
                      }
                    }}
                    min="0" 
                    max="100" 
                    placeholder="0-100"
                  />
                </label>
              </div>
            </div>
          )}
        </div>

        {/* 목줄 섹션 */}
        <div className="equipment-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h2 style={{ margin: 0 }}>목줄 내구도 계산</h2>
            <div style={{ display: 'flex', gap: '5px' }}>
              {[1, 2, 3, 4].map(num => (
                <button
                  key={num}
                  onClick={() => setLeaderCount(num)}
                  style={{
                    padding: '5px 12px',
                    borderRadius: '5px',
                    border: leaderCount === num ? '2px solid #007bff' : '1px solid #ccc',
                    backgroundColor: leaderCount === num ? '#007bff' : 'transparent',
                    color: leaderCount === num ? 'white' : 'inherit',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: leaderCount === num ? 'bold' : 'normal'
                  }}
                >
                  {num}개
                </button>
              ))}
            </div>
          </div>
          <h4
            style={{
              marginLeft: '1.5rem',
              paddingLeft: '0.5rem',
              borderLeft: '4px solid #007bff',
            }}>
            쇼크리더, 리드코어, 목줄의 갯수에 따라 선택 및 입력 해주세요
          </h4>
          {Array.from({ length: leaderCount }).map((_, index) => (
            <div key={index} style={{ 
              marginBottom: '15px',
              padding: '10px',
              backgroundColor: 'var(--hover-bg)',
              borderRadius: '8px',
              border: '1px solid var(--border-color)'
            }}>
              <h3 style={{ fontSize: '14px', marginBottom: '10px', marginTop: 0 }}>목줄 {index + 1}</h3>
              <div style={{ 
                display: 'flex',
                gap: '15px',
                flexWrap: 'wrap',
                justifyContent: 'space-around'
              }}>
                <div style={{ flex: '1 1 200px', minWidth: '150px', maxWidth: '250px' }}>
                  <label style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '5px',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    color: 'var(--text-color)'
                  }}>
                    <span>최대 하중 (kg):</span>
                    <input 
                      type="number" 
                      value={leaders[index].weight} 
                      onChange={(e) => {
                        const newLeaders = [...leaders];
                        newLeaders[index] = { ...newLeaders[index], weight: e.target.value };
                        setLeaders(newLeaders);
                      }}
                      min="0" 
                      step="0.1"
                      placeholder="하중(kg)"
                      style={{
                        width: '100%',
                        padding: '0.5rem 0.75rem',
                        border: '2px solid var(--border-color)',
                        borderRadius: '6px',
                        fontSize: '1rem',
                        backgroundColor: 'var(--card-bg)',
                        color: 'var(--text-color)',
                        boxSizing: 'border-box'
                      }}
                    />
                  </label>
                </div>
                
                <div style={{ flex: '1 1 200px', minWidth: '150px', maxWidth: '250px' }}>
                  <label style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '5px',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    color: 'var(--text-color)'
                  }}>
                    <span>손상도 (%):</span>
                    <input 
                      type="number" 
                      value={leaders[index].damage} 
                      onChange={(e) => {
                        let value = e.target.value;
                        // 빈 값은 허용
                        if (value === '') {
                          const newLeaders = [...leaders];
                          newLeaders[index] = { ...newLeaders[index], damage: '' };
                          setLeaders(newLeaders);
                          return;
                        }
                        // 숫자로 변환하여 0-100 범위로 제한
                        const numValue = parseFloat(value);
                        if (!isNaN(numValue)) {
                          const clampedValue = Math.max(0, Math.min(100, numValue));
                          const newLeaders = [...leaders];
                          newLeaders[index] = { ...newLeaders[index], damage: clampedValue.toString() };
                          setLeaders(newLeaders);
                        }
                      }}
                      min="0" 
                      max="100" 
                      placeholder="0-100"
                      style={{
                        width: '100%',
                        padding: '0.5rem 0.75rem',
                        border: '2px solid var(--border-color)',
                        borderRadius: '6px',
                        fontSize: '1rem',
                        backgroundColor: 'var(--card-bg)',
                        color: 'var(--text-color)',
                        boxSizing: 'border-box'
                      }}
                    />
                  </label>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 우측: 채비 정보 영역 */}
      <div className="tackle-info-area">
        <div className="tackle-info-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2 style={{ margin: 0 }}>채비 정보</h2>
            <button 
              className="language-btn share-btn"
              onClick={() => {
                const url = window.location.href;
                navigator.clipboard.writeText(url).then(() => {
                  toast.success('공유 링크가 클립보드에 복사되었습니다!', {
                    duration: 2000
                  });
                }).catch(err => {
                  console.error('URL 복사 실패:', err);
                  toast.error('URL 복사에 실패했습니다.');
                });
              }}
              title="현재 설정을 URL로 공유"
            >
              🔗 공유
            </button>
          </div>
          
          {/* 위험 상황 배너 */}
          {(() => {
            const dangerCheck = checkDangerSituation();
            return (
              <div>
                {dangerCheck?.dangers && (
                  <div className="danger-alerts">
                    {dangerCheck.dangers.map((danger, index) => (
                      <DangerBanner key={`danger-${index}`} message={danger} />
                    ))}
                  </div>
                )}
                {dangerCheck?.warnings && (
                  <div className="warning-alerts">
                    {dangerCheck.warnings.map((warning, index) => (
                      <WarningBanner key={`warning-${index}`} message={warning} />
                    ))}
                  </div>
                )}
              </div>
            );
          })()}

          {/* 마찰 브레이크 게이지 */}
          {reelDragResult && getUsableDragStage() && (
            <div className="drag-gauge-section">
              <h3>권장 마찰 브레이크 설정</h3>
              <div className="drag-gauge-container">
                <DragGauge 
                  maxStage={getUsableDragStage()} 
                  currentStage={getUsableDragStage()} 
                />
                <div className="drag-info">
                  <p>최대 안전 마찰 브레이크: <strong>{getUsableDragStage()}/29</strong></p>
                  <p>{getUsableDragStage()} 마찰 브레이크시 하중: <strong>{(getUsableDragStage() * reelDragResult.perUnit).toFixed(2)} kg</strong></p>
                  <p>브레이크 1당 하중: <strong>{reelDragResult.perUnit.toFixed(2)} kg</strong></p>
                </div>
              </div>
              
              {/* 텐션 게이지바 */}
              <div className="tension-gauge-container">
                <h4>최대 텐션 하중 게이지 (29 마찰 브레이크)</h4>
                <div className="tension-gauge-wrapper">
                  {(() => {
                    // 모든 목줄의 최소값 구하기
                    const leaderMinWeight = leaderResults
                      .slice(0, leaderCount)
                      .filter(result => result !== null)
                      .reduce((min, result) => Math.min(min, result.remaining), Infinity);
                    
                    // 최소 하중 계산 (낚싯대, 릴 내부하중, 낚싯줄, 목줄 중 최소값)
                    const maxLoad = Math.min(
                      rodResult?.remaining || Infinity,
                      reelCapacityResult?.remaining || Infinity,
                      lineResult?.remaining || Infinity,
                      leaderMinWeight
                    );
                    
                    // 릴 최대저항 (29 마찰 브레이크시 최대 하중)
                    const reelMaxDrag = reelDragResult.remaining;
                    
                    // 최대값이 유효하지 않은 경우 처리
                    if (maxLoad === Infinity || maxLoad <= 0) {
                      return <p>모든 장비를 선택해주세요.</p>;
                    }
                    
                    // 텐션 비율 계산: 릴 최대저항 / 최소 하중 * 100
                    // 예: 최소하중 50kg, 릴최대저항 5kg -> 5/50 * 100 = 10%
                    const tensionRatio = Math.min((reelMaxDrag / maxLoad) * 100, 100);
                    
                    const isDangerous = tensionRatio >= 95;
                    
                    return (
                      <div className={`tension-gauge-bar ${isDangerous ? 'dangerous' : ''}`}>
                        {/* 배경 게이지 */}
                        <div className="gauge-background">
                          {/* 기준선들 - 좌우 대칭 배치 */}
                          <div className="gauge-marker" style={{left: '12.5%'}}></div>
                          <div className="gauge-marker" style={{left: '26%'}}></div>
                          <div className="gauge-marker gauge-marker-center" style={{left: '50%'}}></div>
                          <div className="gauge-marker" style={{left: '74%'}}></div>
                          <div className="gauge-marker" style={{left: '87.5%'}}></div>
                          
                          {/* 실제 텐션 게이지 - 중앙에서 좌우 대칭으로 채워짐 */}
                          <div 
                            className="tension-fill"
                            style={{
                              backgroundColor: getTensionGaugeColor(tensionRatio),
                              boxShadow: tensionRatio >= 95 ? '0 0 10px rgba(239, 68, 68, 0.8)' : 'none',
                              clipPath: `polygon(${Math.max(0, 50 - tensionRatio/2)}% 0, ${Math.min(100, 50 + tensionRatio/2)}% 0, ${Math.min(100, 50 + tensionRatio/2)}% 100%, ${Math.max(0, 50 - tensionRatio/2)}% 100%)`
                            }}
                          ></div>
                          
                          {/* 중앙 시작점은 마커로 대체됨 */}
                        </div>
                        
                        {/* 게이지 정보 */}
                        <div className="gauge-info">
                          <div className="gauge-values">
                            <span>최소 장비 하중: <strong>{maxLoad.toFixed(2)} kg</strong></span>
                            <span>릴 최대저항: <strong>{reelMaxDrag.toFixed(2)} kg</strong></span>
                            <span>텐션 게이지: <strong>{tensionRatio.toFixed(1)}%</strong> 
                              {tensionRatio < 95 && <span className="safety-level safety">안전</span>}
                              {tensionRatio >= 95 && tensionRatio < 100 && <span className="safety-level caution">주의</span>}
                              {tensionRatio >= 100 && <span className="safety-level danger">위험</span>}
                            </span>
                          </div>
                          {tensionRatio >= 100 && (
                            <div className="danger-warning">
                              ⚠️ 매우 위험! 장비 파손 위험이 높습니다!
                            </div>
                          )}
                          {tensionRatio >= 95 && tensionRatio < 100 && (
                            <div className="caution-warning">
                              ⚠️ 주의! 장비 하중이 높습니다.
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          )}

          {/* 계산된 하중 표시 */}
          <div className="calculated-weights">
            <h3>계산된 하중</h3>
            <div className="weight-grid">
              <div className="weight-item">
                <span className="weight-label">낚싯대:</span>
                <span className="weight-value">
                  {rodResult ? `${rodResult.remaining.toFixed(2)} kg` : '미계산'}
                </span>
              </div>
              <div className="weight-item">
                <span className="weight-label">릴 최대저항:</span>
                <span className="weight-value">
                  {reelDragResult ? `${reelDragResult.remaining.toFixed(2)} kg` : '미계산'}
                </span>
              </div>
              <div className="weight-item">
                <span className="weight-label">릴 내부하중:</span>
                <span className="weight-value">
                  {reelCapacityResult ? `${reelCapacityResult.remaining.toFixed(2)} kg` : '미계산'}
                </span>
              </div>
              <div className="weight-item">
                <span className="weight-label">낚싯줄:</span>
                <span className="weight-value">
                  {lineResult ? `${lineResult.remaining.toFixed(2)} kg` : '미계산'}
                </span>
              </div>
              {leaderResults.slice(0, leaderCount).map((result, index) => (
                result && (
                  <div key={index} className="weight-item">
                    <span className="weight-label">목줄{index + 1}:</span>
                    <span className="weight-value">
                      {result.remaining.toFixed(2)} kg
                    </span>
                  </div>
                )
              ))}
            </div>
          </div>

          {/* 채비 내구도 요약 */}
          <div className="tackle-summary">
            <h3>채비 내구도 요약</h3>
            
            {/* 낚싯대 */}
            {selectedRod && (
              <div className="tackle-durability-item">
                <div className="tackle-item-header">
                  <span className="tackle-name">낚싯대:</span>
                  <span className="tackle-percentage">
                    {rodResult ? `${rodResult.percentage.toFixed(1)}%` : '미계산'}
                  </span>
                </div>
                {rodResult && (
                  <div className="mini-progress-bar-container">
                    <div 
                      className="mini-progress-bar" 
                      style={{ 
                        width: `${rodResult.percentage}%`, 
                        backgroundColor: getBarColor(rodResult.percentage) 
                      }}
                    ></div>
                  </div>
                )}
              </div>
            )}

            {/* 릴 마찰 브레이크 */}
            {selectedReel && reelDragResult && (
              <div className="tackle-durability-item">
                <div className="tackle-item-header">
                  <span className="tackle-name">릴 마찰 브레이크:</span>
                  <span className="tackle-percentage">{reelDragResult.percentage.toFixed(1)}%</span>
                </div>
                <div className="mini-progress-bar-container">
                  <div 
                    className="mini-progress-bar" 
                    style={{ 
                      width: `${reelDragResult.percentage}%`, 
                      backgroundColor: getBarColor(reelDragResult.percentage) 
                    }}
                  ></div>
                </div>
              </div>
            )}

            {/* 릴 내부 하중 */}
            {selectedReel && reelCapacityResult && (
              <div className="tackle-durability-item">
                <div className="tackle-item-header">
                  <span className="tackle-name">릴 내부 하중:</span>
                  <span className="tackle-percentage">{reelCapacityResult.percentage.toFixed(1)}%</span>
                </div>
                <div className="mini-progress-bar-container">
                  <div 
                    className="mini-progress-bar" 
                    style={{ 
                      width: `${reelCapacityResult.percentage}%`, 
                      backgroundColor: getBarColor(reelCapacityResult.percentage) 
                    }}
                  ></div>
                </div>
              </div>
            )}

            {/* 낚싯줄 */}
            {selectedLine && (
              <div className="tackle-durability-item">
                <div className="tackle-item-header">
                  <span className="tackle-name">낚싯줄:</span>
                  <span className="tackle-percentage">
                    {lineResult ? `${lineResult.percentage.toFixed(1)}%` : '미계산'}
                  </span>
                </div>
                {lineResult && (
                  <div className="mini-progress-bar-container">
                    <div 
                      className="mini-progress-bar" 
                      style={{ 
                        width: `${lineResult.percentage}%`, 
                        backgroundColor: getBarColor(lineResult.percentage) 
                      }}
                    ></div>
                  </div>
                )}
              </div>
            )}

            {/* 목줄 */}
            {leaderResults.slice(0, leaderCount).map((result, index) => (
              result && (
                <div key={index} className="tackle-durability-item">
                  <div className="tackle-item-header">
                    <span className="tackle-name">목줄{index + 1}:</span>
                    <span className="tackle-percentage">
                      {result.percentage.toFixed(1)}%
                    </span>
                  </div>
                  <div className="mini-progress-bar-container">
                    <div 
                      className="mini-progress-bar" 
                      style={{ 
                        width: `${result.percentage}%`, 
                        backgroundColor: getBarColor(result.percentage) 
                      }}
                    ></div>
                  </div>
                </div>
              )
            ))}
          </div>
        </div>
      </div>
    </div>
      </div>

      <Comments pageName="duration" />

      {/* 중복 이름 확인 모달 */}
      {showDuplicateModal && (
        <div className="modal-overlay" onClick={cancelOverwrite}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>중복된 이름</h3>
            <p>'{duplicateSettingName}' 이름이 이미 존재합니다.</p>
            <p>덮어쓰시겠습니까?</p>
            <div className="modal-buttons">
              <button className="modal-confirm-btn" onClick={confirmOverwrite}>
                덮어쓰기
              </button>
              <button className="modal-cancel-btn" onClick={cancelOverwrite}>
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 삭제 확인 모달 */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={cancelDeleteSetting}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>설정 삭제</h3>
            <p>정말로 이 설정을 삭제하시겠습니까?</p>
            <div className="modal-buttons">
              <button className="modal-confirm-btn" onClick={confirmDeleteSetting}>
                삭제
              </button>
              <button className="modal-cancel-btn" onClick={cancelDeleteSetting}>
                취소
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DurationPage;